<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="800px"
    @keydown.esc="close"
  >
    <v-card class="payment-method-modal">
      <v-card-title class="payment-modal-header">
        <h2 class="gradient-text">Odaberi način plaćanja</h2>
        <v-spacer></v-spacer>
        <v-btn icon @click="close" class="close-btn">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="payment-modal-content">
        <v-container>
          <v-row class="justify-center">
            <!-- GOTOVINA (G) -->
            <v-col cols="12" sm="4" class="d-flex justify-center">
              <div class="payment-btn-wrapper">
                <v-btn
                  class="payment-method-btn cash-btn"
                  :disabled="!buttonsEnabled"
                  @click="selectPaymentMethod('cash')"
                  elevation="4"
                  x-large
                  block
                >
                  <div class="btn-content">
                    <v-icon size="64" class="mb-3">mdi-currency-eur</v-icon>
                    <div class="btn-text">GOTOVINA</div>
                  </div>
                </v-btn>
                <!-- Keyboard shortcut badge -->
                <v-chip
                  v-if="showShortcutBadges || showBadgesTemporarily"
                  class="shortcut-badge"
                  color="white"
                  text-color="#2C5A90"
                >
                  G
                </v-chip>
              </div>
            </v-col>

            <!-- KARTICA (K) -->
            <v-col cols="12" sm="4" class="d-flex justify-center">
              <div class="payment-btn-wrapper">
                <v-btn
                  class="payment-method-btn card-btn"
                  :disabled="!buttonsEnabled"
                  @click="selectPaymentMethod('card')"
                  elevation="4"
                  x-large
                  block
                >
                  <div class="btn-content">
                    <v-icon size="64" class="mb-3">mdi-credit-card-outline</v-icon>
                    <div class="btn-text">KARTICA</div>
                  </div>
                </v-btn>
                <!-- Keyboard shortcut badge -->
                <v-chip
                  v-if="showShortcutBadges || showBadgesTemporarily"
                  class="shortcut-badge"
                  color="white"
                  text-color="#3B78C3"
                >
                  K
                </v-chip>
              </div>
            </v-col>

            <!-- R1 (O) -->
            <v-col cols="12" sm="4" class="d-flex justify-center">
              <div class="payment-btn-wrapper">
                <v-btn
                  class="payment-method-btn r1-btn"
                  :disabled="!buttonsEnabled"
                  @click="selectPaymentMethod('r1')"
                  elevation="4"
                  x-large
                  block
                >
                  <div class="btn-content">
                    <v-icon size="64" class="mb-3">mdi-file-document-outline</v-icon>
                    <div class="btn-text">R1</div>
                  </div>
                </v-btn>
                <!-- Keyboard shortcut badge -->
                <v-chip
                  v-if="showShortcutBadges || showBadgesTemporarily"
                  class="shortcut-badge"
                  color="white"
                  text-color="#6154A4"
                >
                  O
                </v-chip>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'PaymentMethodDialog',

  props: {
    buttonsEnabled: {
      type: Boolean,
      default: true
    },
    showShortcutBadges: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      dialog: false,
      showBadgesTemporarily: false
    }
  },

  methods: {
    open () {
      this.dialog = true
      // Add keyboard listeners when dialog opens
      document.addEventListener('keydown', this.handleKeyPress)
      document.addEventListener('keydown', this.handleSpaceKeyDown)
      document.addEventListener('keyup', this.handleSpaceKeyUp)
    },

    close () {
      this.dialog = false
      this.showBadgesTemporarily = false
      // Remove keyboard listeners when dialog closes
      document.removeEventListener('keydown', this.handleKeyPress)
      document.removeEventListener('keydown', this.handleSpaceKeyDown)
      document.removeEventListener('keyup', this.handleSpaceKeyUp)
    },

    handleKeyPress (e) {
      // Ignore if typing in an input field
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return

      const key = e.key.toUpperCase()

      switch (key) {
        case 'G': // Gotovina
          if (this.buttonsEnabled) {
            e.preventDefault()
            e.stopPropagation()
            this.selectPaymentMethod('cash')
          }
          break
        case 'K': // Kartica
          if (this.buttonsEnabled) {
            e.preventDefault()
            e.stopPropagation()
            this.selectPaymentMethod('card')
          }
          break
        case 'O': // R1
          if (this.buttonsEnabled) {
            e.preventDefault()
            e.stopPropagation()
            this.selectPaymentMethod('r1')
          }
          break
      }
    },

    handleSpaceKeyDown (e) {
      if (e.key !== ' ') return
      e.preventDefault()
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return
      this.showBadgesTemporarily = true
    },

    handleSpaceKeyUp (e) {
      if (e.key !== ' ') return
      e.preventDefault()
      this.showBadgesTemporarily = false
    },

    selectPaymentMethod (method) {
      this.$emit('payment-method-selected', method)
      this.close()
    }
  },

  beforeDestroy () {
    // Cleanup keyboard listeners
    document.removeEventListener('keydown', this.handleKeyPress)
    document.removeEventListener('keydown', this.handleSpaceKeyDown)
    document.removeEventListener('keyup', this.handleSpaceKeyUp)
  }
}
</script>

<style scoped>
/* Payment Modal Card */
.payment-method-modal {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 16px !important;
  overflow: hidden;
}

/* Modal Header with Gradient */
.payment-modal-header {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.9) 0%,
    rgba(57, 137, 201, 0.85) 50%,
    rgba(97, 84, 164, 0.9) 100%) !important;
  padding: 24px 24px 20px !important;
  position: relative;
}

.payment-modal-header .gradient-text {
  font-size: 28px !important;
  font-weight: 700 !important;
  color: white !important;
  background: none !important;
  -webkit-text-fill-color: white !important;
  letter-spacing: 0.5px !important;
  margin: 0 !important;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 50% !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3) !important;
  transform: rotate(90deg) scale(1.1);
}

/* Modal Content */
.payment-modal-content {
  padding: 48px 24px !important;
}

/* Payment Button Wrapper (for badge positioning) */
.payment-btn-wrapper {
  position: relative;
  width: 100%;
  max-width: 240px;
}

/* Base Payment Button Style */
.payment-method-btn {
  border-radius: 16px !important;
  border: 2px solid rgba(255, 255, 255, 0.4) !important;
  backdrop-filter: blur(10px) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  text-transform: none !important;
  padding: 32px 24px !important;
  min-height: 220px !important;
  position: relative;
}

.payment-method-btn:hover:not(:disabled) {
  transform: translateY(-8px) scale(1.05);
  border-width: 3px !important;
}

.payment-method-btn:disabled {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
}

.btn-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.btn-text {
  font-size: 20px !important;
  font-weight: 700 !important;
  letter-spacing: 1px !important;
  color: white !important;
}

/* Cash Button (Blue) */
.cash-btn {
  background: linear-gradient(135deg,
    rgba(44, 90, 144, 0.95) 0%,
    rgba(33, 67, 108, 0.9) 100%) !important;
  box-shadow: 0 8px 24px rgba(44, 90, 144, 0.4) !important;
}

.cash-btn:hover:not(:disabled) {
  background: linear-gradient(135deg,
    rgba(44, 90, 144, 1) 0%,
    rgba(33, 67, 108, 1) 100%) !important;
  box-shadow: 0 12px 32px rgba(44, 90, 144, 0.6) !important;
  border-color: rgba(255, 255, 255, 0.6) !important;
}

/* Card Button (Cyan) */
.card-btn {
  background: linear-gradient(135deg,
    rgba(59, 120, 195, 0.95) 0%,
    rgba(41, 91, 144, 0.9) 100%) !important;
  box-shadow: 0 8px 24px rgba(59, 120, 195, 0.4) !important;
}

.card-btn:hover:not(:disabled) {
  background: linear-gradient(135deg,
    rgba(59, 120, 195, 1) 0%,
    rgba(41, 91, 144, 1) 100%) !important;
  box-shadow: 0 12px 32px rgba(59, 120, 195, 0.6) !important;
  border-color: rgba(255, 255, 255, 0.6) !important;
}

/* R1 Button (Purple) */
.r1-btn {
  background: linear-gradient(135deg,
    rgba(97, 84, 164, 0.95) 0%,
    rgba(77, 64, 144, 0.9) 100%) !important;
  box-shadow: 0 8px 24px rgba(97, 84, 164, 0.4) !important;
}

.r1-btn:hover:not(:disabled) {
  background: linear-gradient(135deg,
    rgba(97, 84, 164, 1) 0%,
    rgba(77, 64, 144, 1) 100%) !important;
  box-shadow: 0 12px 32px rgba(97, 84, 164, 0.6) !important;
  border-color: rgba(255, 255, 255, 0.6) !important;
}

/* Shortcut Badge */
.shortcut-badge {
  position: absolute !important;
  top: 12px;
  right: 12px;
  font-size: 18px !important;
  font-weight: 700 !important;
  padding: 8px 12px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 50% !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
  border: 2px solid rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px) !important;
}

/* Icon Styles */
.payment-method-btn .v-icon {
  color: white !important;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.payment-method-btn:hover:not(:disabled) .v-icon {
  transform: scale(1.1);
  filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.4));
}

/* Mobile Responsiveness */
@media (max-width: 959px) {
  .payment-method-btn {
    min-height: 180px !important;
    padding: 24px 16px !important;
  }

  .payment-method-btn .v-icon {
    font-size: 56px !important;
  }

  .btn-text {
    font-size: 18px !important;
  }

  .shortcut-badge {
    font-size: 16px !important;
    height: 32px !important;
    min-width: 32px !important;
  }
}

@media (max-width: 599px) {
  .payment-modal-content {
    padding: 24px 16px !important;
  }

  .payment-method-btn {
    min-height: 160px !important;
    padding: 20px 12px !important;
  }

  .payment-method-btn .v-icon {
    font-size: 48px !important;
  }

  .btn-text {
    font-size: 16px !important;
  }
}
</style>
